/**
 * @author Gananath Athukorala
 * @version 0.1
 */

import React, { useState } from "react";
import labelsEN from "../../../locales/en/translation.json";
import StorageService from "../services/StorageService";
import ResetPasswordModal from "../credentialManager/components/ResetPasswordModal";
import {
  Alert,
  Box,
  Grid,
  InputLabel,
  TextField,
  Fab,
  Tooltip,
} from "@mui/material";
import { LockReset, Save } from "@mui/icons-material";
import LoadingButton from "@mui/lab/LoadingButton";
import api from "../services/AuthApi";
import Helpers from "../utils/Helpers";

const MyProfile = () => {
  const values = StorageService.getUserData();
  const [userData, setUserData] = useState({
    userRole: values.userRole === "-" ? null : values.userRole,
    username: values.username === "-" ? null : values.username,
    firstName: values.firstName === "-" ? null : values.firstName,
    lastName: values.lastName === "-" ? null : values.lastName,
    displayName: values.displayName === "-" ? null : values.displayName,
    referenceNo: values.referenceNo === "-" ? null : values.referenceNo,
    primEmail: values.primEmail === "-" ? null : values.primEmail,
    secEmail: values.secEmail === "-" ? null : values.secEmail,
    primContact: values.primContact === "-" ? null : values.primContact,
    secContact: values.secContact === "-" ? null : values.secContact,
    primAddress: values.primAddress === "-" ? null : values.primAddress,
    secAddress: values.secAddress === "-" ? null : values.secAddress,
  });
  const [updatedValues, setUpdatedValues] = useState({
    username: values.username,
  });

  const [updateModalOpen, setUpdateModalOpen] = useState(false);
  const [isValid, setIsValid] = useState(true);
  const [isPrimContactValid, setIsPrimContactValid] = useState(false);
  const [isSecContactValid, setIsSecContactValid] = useState(false);
  const [isSubmit, setIsSubmit] = useState(false);
  const [errors, setErrors] = useState({
    primContact: "",
    secContact: "",
  });
  const [responseDesc, setResponseDesc] = useState({
    code: null,
    response: null,
    description: null,
  });

  // Change fields data
  const handleOnChange = (event) => {
    const { name, value } = event.target;
    setUserData({
      ...userData,
      [name]: value,
    });
    setUpdatedValues({
      ...updatedValues,
      [name]: value,
    });
  };

  const validatePrimeContact = Helpers.validateContact(
    updatedValues.primContact
  );
  const validateSecContact = Helpers.validateContact(updatedValues.secContact);

  const postConfig = {
    headers: {
      "Content-Type": "multipart/form-data",
    },
  };

  const handleSaveRowEdits = (event) => {
    event.preventDefault();
    setErrors({
      primContact: "",
      secContact: "",
    });
    setIsValid(true);
    setIsPrimContactValid(false);
    setIsSecContactValid(false);

    if (validatePrimeContact) {
      setErrors({
        primContact: "Please enter a valid phone number (e.g. 94123456789)",
      });
      setIsPrimContactValid(true);
      setIsValid(false);
    }

    if (validateSecContact) {
      setErrors({
        secContact: "Please enter a valid phone number (e.g. 94123456789)",
      });
      setIsSecContactValid(true);
      setIsValid(false);
    }

    if (isValid) {
      setIsSubmit(true);
      const updatedUser = new Blob([JSON.stringify(updatedValues)], {
        type: "application/json",
      });
      const formData = new FormData();
      formData.append("profile_data", updatedUser);

      api
        .put("/users", formData, postConfig)
        .then((res) => {
          setResponseDesc(res?.data);
          setTimeout(() => {
            handleClearData();
          }, 3000);
          console.log("code !", res?.data.code);
        })
        .catch((err) => {
          setIsSubmit(false);
          setResponseDesc(err.response?.data);
        });
    }
  };

  const handleClearData = () => {
    setResponseDesc({
      code: null,
      response: null,
      description: null,
    });
    setErrors({
      primContact: "",
      secContact: "",
    });
    setIsValid(true);
    setIsPrimContactValid(false);
    setIsSecContactValid(false);
    setIsSubmit(false);
  };

  const responseCode = responseDesc?.code;

  return (
    <>
      <h1>{labelsEN.app_myprofile_page_title}</h1>
      <br />
      <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
        <Tooltip title="Password Reset">
          <Fab
            color="success"
            aria-label="reset"
            size="large"
            onClick={() => setUpdateModalOpen(true)}>
            <LockReset />
          </Fab>
        </Tooltip>
      </Box>
      {responseDesc.code && (
        <Alert
          style={{ marginTop: "16px", marginBottom: "16px" }}
          severity={responseCode === 200 ? "success" : "error"}
          responseCode={responseCode}>
          {responseDesc.description}
        </Alert>
      )}
      <form onSubmit={handleSaveRowEdits}>
        <Box display="flex" justifyContent="center" alignItems="center">
          <Grid container spacing={2} width="100%">
            <Grid item xs={6} mt={2}>
              <InputLabel shrink={userData[0]?.userRole !== ""}>
                User Role
              </InputLabel>
              <TextField
                fullWidth
                disabled
                variant="outlined"
                name="userRole"
                value={userData.userRole ? userData.userRole : ""}
                required
              />
            </Grid>
            <Grid item xs={6} mt={2}>
              <InputLabel shrink={userData?.username !== ""}>
                User Name
              </InputLabel>
              <TextField
                fullWidth
                disabled
                variant="outlined"
                name="username"
                value={userData.username ? userData.username : ""}
                required
              />
            </Grid>
            <Grid item xs={6}>
              <InputLabel shrink={userData?.firstName !== ""}>
                First Name
              </InputLabel>
              <TextField
                fullWidth
                onChange={handleOnChange}
                variant="outlined"
                name="firstName"
                value={userData.firstName ? userData.firstName : ""}
                required
              />
            </Grid>
            <Grid item xs={6}>
              <InputLabel shrink={userData?.lastName !== ""}>
                Last Name
              </InputLabel>
              <TextField
                required
                fullWidth
                onChange={handleOnChange}
                variant="outlined"
                name="lastName"
                value={userData.lastName ? userData.lastName : ""}
              />
            </Grid>
            <Grid item xs={6}>
              <InputLabel shrink={userData?.displayName !== ""}>
                Display Name
              </InputLabel>
              <TextField
                fullWidth
                onChange={handleOnChange}
                variant="outlined"
                name="displayName"
                value={userData.displayName ? userData.displayName : ""}
              />
            </Grid>
            <Grid item xs={6}>
              <InputLabel shrink={userData?.referenceNo !== ""}>
                Reference No
              </InputLabel>
              <TextField
                fullWidth
                onChange={handleOnChange}
                variant="outlined"
                name="referenceNo"
                value={userData.referenceNo ? userData.referenceNo : ""}
              />
            </Grid>
            <Grid item xs={6}>
              <InputLabel shrink={userData?.primEmail !== ""}>
                Primary Email Address
              </InputLabel>
              <TextField
                fullWidth
                onChange={handleOnChange}
                variant="outlined"
                type="email"
                name="primEmail"
                value={userData.primEmail ? userData.primEmail : ""}
              />
            </Grid>
            <Grid item xs={6}>
              <InputLabel shrink={userData?.secEmail !== ""}>
                Secondary Email Address
              </InputLabel>
              <TextField
                fullWidth
                onChange={handleOnChange}
                variant="outlined"
                type="email"
                name="secEmail"
                value={userData.secEmail ? userData.secEmail : ""}
              />
            </Grid>
            <Grid item xs={6}>
              <InputLabel shrink={userData?.primContact !== ""}>
                Primary Contact
              </InputLabel>
              <TextField
                fullWidth
                placeholder="Enter Contact 94 xxx xxx xxx"
                id="inputPrimContact"
                variant="outlined"
                name="primContact"
                value={userData.primContact ? userData.primContact : ""}
                onChange={handleOnChange}
                error={isPrimContactValid}
                helperText={errors.primContact}
              />
            </Grid>
            <Grid item xs={6}>
              <InputLabel shrink={userData?.secContact !== ""}>
                Secondary Contact
              </InputLabel>
              <TextField
                fullWidth
                placeholder="Enter Contact 94 xxx xxx xxx"
                id="inputSecContact"
                variant="outlined"
                name="secContact"
                value={userData.secContact ? userData.secContact : ""}
                onChange={handleOnChange}
                error={isSecContactValid}
                helperText={errors.primContact}
              />
            </Grid>
            <Grid item xs={6} mb={2}>
              <InputLabel shrink={userData?.primAddress !== ""}>
                Primary Address
              </InputLabel>
              <TextField
                fullWidth
                onChange={handleOnChange}
                variant="outlined"
                name="primAddress"
                value={userData.primAddress ? userData.primAddress : ""}
              />
            </Grid>
            <Grid item xs={6} mb={2}>
              <InputLabel shrink={userData?.secAddress !== ""}>
                Secondary Address
              </InputLabel>
              <TextField
                fullWidth
                onChange={handleOnChange}
                variant="outlined"
                name="secAddress"
                value={userData.secAddress ? userData.secAddress : ""}
              />
            </Grid>
          </Grid>
        </Box>
        <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
          <LoadingButton
            loading={isSubmit}
            loadingPosition="start"
            startIcon={<Save />}
            variant="outlined"
            color="success"
            type="submit">
            Update
          </LoadingButton>
        </Box>
      </form>
      <ResetPasswordModal
        username={userData.username}
        open={updateModalOpen}
        onClose={() => {
          setUpdateModalOpen(false);
        }}
      />
    </>
  );
};

export default MyProfile;
